/**
 * <s-product-gallery> drives the product page thumbnail & carousel gallery.
 * The track scrolls natively (swipe / trackpad); this element adds arrow and
 * thumbnail navigation and keeps the active thumbnail in sync with the visible slide.
 *
 * Required markup:
 *   [data-gallery-track]  — Horizontal scroll-snap container.
 *   [data-gallery-slide]  — One per media item, direct children of the track.
 *   [data-gallery-prev] / [data-gallery-next] — Optional arrow buttons.
 *   [data-gallery-thumb="<index>"] — Optional thumbnail buttons; aria-current marks the active one.
 */
class ProductGallery extends HTMLElement {
  #ac = null;
  #observer = null;
  #index = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      if (target.closest('[data-gallery-prev]')) {
        this.go(this.#index - 1);
      } else if (target.closest('[data-gallery-next]')) {
        this.go(this.#index + 1);
      } else {
        const thumb = target.closest('[data-gallery-thumb]');
        if (thumb) this.go(Number(thumb.dataset.galleryThumb));
      }
    }, opts);

    const track = this.#track();
    if (!track) return;

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          this.#setActive(this.#slides().indexOf(entry.target));
        }
      }
    }, { root: track, threshold: [0.6] });

    for (const slide of this.#slides()) this.#observer.observe(slide);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  go(index) {
    const slides = this.#slides();
    const track = this.#track();
    if (!slides.length || !track) return;

    const count = slides.length;
    const next = ((index % count) + count) % count;
    const slide = slides[next];
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
    this.#setActive(next);
  }

  #track() {
    return this.querySelector('[data-gallery-track]');
  }

  #slides() {
    const track = this.#track();
    return track ? Array.from(track.querySelectorAll(':scope > [data-gallery-slide]')) : [];
  }

  #setActive(index) {
    if (index < 0) return;
    this.#index = index;

    for (const thumb of this.querySelectorAll('[data-gallery-thumb]')) {
      const active = Number(thumb.dataset.galleryThumb) === index;
      if (active) {
        thumb.setAttribute('aria-current', 'true');
        thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      } else {
        thumb.removeAttribute('aria-current');
      }
    }
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', ProductGallery);
}
